import {
  ArrowBack,
  CloudDownload as ScrapeIcon,
  MusicNote as MusicIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Shuffle as ShuffleIcon,
} from "@mui/icons-material";
import type { ChipProps } from "@mui/material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ScrapeModal from "../Components/ScrapeModal";
import { apiFetchArtist, ArtistDetail } from "../utils/useDb";
import { DEFAULT_PAGE_TITLE, useDocumentTitle } from "../utils/titleUtils";

export default function ArtistPage() {
  const location = useLocation();
  const params = useParams<{ artistName?: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  // Determine artist name from route param or query param
  const queryParams = new URLSearchParams(location.search);
  const artistNameParam = params.artistName || queryParams.get("name") || "";

  // Data state
  const [artist, setArtist] = useState<ArtistDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isScrapeOpen, setIsScrapeOpen] = useState(false);

  const artistTitle = useMemo(() => {
    if (artist?.name) {
      return `${artist.name} - Chords & Tabs | ${DEFAULT_PAGE_TITLE}`;
    }
    return DEFAULT_PAGE_TITLE;
  }, [artist?.name]);

  useDocumentTitle(artistTitle);

  // Filter & Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("all");
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("title_asc");

  const loadArtistData = useCallback(async () => {
    if (!artistNameParam.trim()) {
      setIsLoading(false);
      setError("No artist specified");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiFetchArtist(artistNameParam.trim());
      if (!data) {
        setError(`Artist "${artistNameParam}" not found.`);
        setArtist(null);
      } else {
        setArtist(data);
      }
    } catch (err: any) {
      console.error("Failed to load artist details:", err);
      setError(err.message || "Failed to load artist details.");
    } finally {
      setIsLoading(false);
    }
  }, [artistNameParam]);

  useEffect(() => {
    loadArtistData();
  }, [loadArtistData]);

  // Filter and sort songs
  const filteredSongs = useMemo(() => {
    if (!artist || !artist.songs) return [];

    let list = [...artist.songs];

    // Filter by search query
    if (searchTerm.trim()) {
      const query = searchTerm.trim().toLowerCase();
      list = list.filter(
        (song) =>
          (song.title && song.title.toLowerCase().includes(query)) ||
          (song.key && song.key.toLowerCase().includes(query)) ||
          (song.tags && song.tags.some((t) => t.toLowerCase().includes(query)))
      );
    }

    // Filter by difficulty
    if (selectedDifficulty !== "all") {
      list = list.filter((song) => (song.difficulty || "").toLowerCase() === selectedDifficulty.toLowerCase());
    }

    // Filter by tag
    if (selectedTag !== "all") {
      list = list.filter((song) => song.tags && song.tags.includes(selectedTag));
    }

    // Sort songs
    list.sort((a, b) => {
      if (sortBy === "title_asc") {
        return (a.title || "").localeCompare(b.title || "");
      }
      if (sortBy === "title_desc") {
        return (b.title || "").localeCompare(a.title || "");
      }
      if (sortBy === "difficulty") {
        const diffWeight: Record<string, number> = {
          beginner: 1,
          intermediate: 2,
          advanced: 3,
          expert: 4,
        };
        const weightA = diffWeight[(a.difficulty || "").toLowerCase()] || 0;
        const weightB = diffWeight[(b.difficulty || "").toLowerCase()] || 0;
        return weightA - weightB;
      }
      if (sortBy === "newest") {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      }
      return 0;
    });

    return list;
  }, [artist, searchTerm, selectedDifficulty, selectedTag, sortBy]);

  const getDifficultyColor = (d: string | undefined): ChipProps["color"] => {
    switch ((d || "").toLowerCase()) {
      case "beginner":
        return "success";
      case "intermediate":
        return "warning";
      case "advanced":
      case "expert":
        return "error";
      default:
        return "default";
    }
  };

  const getOriginalWebsite = (url: string) => {
    if (!url) return "ChordMaster";
    if (url.includes("tab4u")) return "Tab4U";
    if (url.includes("ultimate-guitar")) return "Ultimate Guitar";
    return "Source";
  };

  const handlePlayRandomSong = () => {
    if (!artist || !artist.songs || artist.songs.length === 0) return;
    const randomIndex = Math.floor(Math.random() * artist.songs.length);
    const randomSong = artist.songs[randomIndex];
    navigate(`/Song?id=${randomSong.id}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 6 }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" mb={4}>
            <IconButton onClick={() => navigate("/Explore")} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ bgcolor: "grey.300", height: 32, width: 200, borderRadius: 1 }} />
          </Box>
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <SkeletonCard />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error || !artist) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 8 }}>
        <Container maxWidth="md">
          <Card variant="outlined" sx={{ textAlign: "center", p: 6, borderRadius: 3 }}>
            <PersonIcon color="disabled" sx={{ fontSize: 72, mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {error || "Artist Not Found"}
            </Typography>
            <Typography color="text.secondary" mb={4}>
              We couldn't find songs for this artist in the library. You can import new songs or return to explore.
            </Typography>
            <Box display="flex" justifyContent="center" gap={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate("/Explore")}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Back to Explore
              </Button>
              <Button
                variant="contained"
                startIcon={<ScrapeIcon />}
                onClick={() => setIsScrapeOpen(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                }}
              >
                Import Song by URL
              </Button>
            </Box>
          </Card>
        </Container>
        <ScrapeModal
          open={isScrapeOpen}
          onClose={() => setIsScrapeOpen(false)}
          onSuccess={() => loadArtistData()}
        />
      </Box>
    );
  }

  const artistInitials = (artist.name || "A")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
      <Container maxWidth="lg">
        {/* Navigation & Back Button */}
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/Explore")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              ":hover": { color: "primary.main" },
            }}
          >
            Explore
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
            /
          </Typography>
          <Typography variant="body2" color="text.primary" fontWeight="bold">
            {artist.name}
          </Typography>
        </Box>

        {/* Artist Header Hero Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            variant="outlined"
            sx={{
              mb: 4,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
              boxShadow: "0 4px 20px rgba(37, 99, 235, 0.08)",
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, sm: "auto" }}>
                <Avatar
                  sx={{
                    width: { xs: 80, md: 100 },
                    height: { xs: 80, md: 100 },
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontWeight: "bold",
                    background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                    boxShadow: "0 8px 24px rgba(79, 70, 229, 0.3)",
                    mx: { xs: "auto", sm: 0 },
                  }}
                >
                  {artistInitials}
                </Avatar>
              </Grid>
              <Grid size={{ xs: 12, sm: "grow" }}>
                <Box textAlign={{ xs: "center", sm: "left" }}>
                  <Typography
                    variant="h3"
                    component="h1"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1.8rem", sm: "2.3rem", md: "2.8rem" },
                      background: "linear-gradient(90deg, #1e293b, #334155)",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {artist.name}
                  </Typography>

                  {/* Metadata Chips */}
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                    mt={1.5}
                    justifyContent={{ xs: "center", sm: "flex-start" }}
                  >
                    <Chip
                      icon={<MusicIcon sx={{ fontSize: 16 }} />}
                      label={`${artist.song_count} ${
                        artist.song_count === 1 ? "Song" : "Songs"
                      }`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    {artist.difficulties &&
                      artist.difficulties.map((diff) => (
                        <Chip
                          key={diff}
                          label={diff}
                          color={getDifficultyColor(diff)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    {artist.tags &&
                      artist.tags.slice(0, 4).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ bgcolor: "white" }}
                        />
                      ))}
                  </Box>
                </Box>
              </Grid>

              {/* Action Buttons */}
              <Grid size={{ xs: 12, md: "auto" }}>
                <Box
                  display="flex"
                  flexDirection={{ xs: "row", sm: "row", md: "column" }}
                  gap={1.5}
                  justifyContent={{ xs: "center", md: "flex-end" }}
                  width="100%"
                >
                  <Button
                    variant="contained"
                    startIcon={<ShuffleIcon />}
                    onClick={handlePlayRandomSong}
                    disabled={!artist.songs || artist.songs.length === 0}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #5e35b1 0%, #7c3aed 100%)",
                      boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
                      ":hover": {
                        background: "linear-gradient(135deg, #512da8 0%, #6d28d9 100%)",
                      },
                    }}
                  >
                    Random Song
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ScrapeIcon />}
                    onClick={() => setIsScrapeOpen(true)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: "#10b981",
                      color: "#059669",
                      ":hover": {
                        borderColor: "#059669",
                        bgcolor: "rgba(16, 185, 129, 0.05)",
                      },
                    }}
                  >
                    Add Song
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </motion.div>

        {/* Filter & Search Bar */}
        <Card variant="outlined" sx={{ mb: 4, p: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search within artist */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box position="relative" display="flex" alignItems="center">
                <SearchIcon
                  sx={{
                    position: "absolute",
                    left: 12,
                    color: "text.disabled",
                    pointerEvents: "none",
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder={`Search songs by ${artist.name}...`}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  InputProps={{ sx: { pl: 4.5 } }}
                />
              </Box>
            </Grid>

            {/* Difficulty Filter */}
            <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={selectedDifficulty}
                  label="Difficulty"
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                >
                  <MenuItem value="all">All Difficulties</MenuItem>
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                  <MenuItem value="expert">Expert</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Tag Filter (if tags exist) */}
            {artist.tags && artist.tags.length > 0 && (
              <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tag / Genre</InputLabel>
                  <Select
                    value={selectedTag}
                    label="Tag / Genre"
                    onChange={(e) => setSelectedTag(e.target.value)}
                  >
                    <MenuItem value="all">All Tags</MenuItem>
                    {artist.tags.map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Sort By */}
            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="title_asc">Title (A-Z)</MenuItem>
                  <MenuItem value="title_desc">Title (Z-A)</MenuItem>
                  <MenuItem value="difficulty">Difficulty</MenuItem>
                  <MenuItem value="newest">Newest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* Results Count Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          px={0.5}
        >
          <Typography variant="h6" fontWeight="bold">
            Songs ({filteredSongs.length})
          </Typography>
          {(searchTerm ||
            selectedDifficulty !== "all" ||
            selectedTag !== "all") && (
            <Button
              size="small"
              onClick={() => {
                setSearchTerm("");
                setSelectedDifficulty("all");
                setSelectedTag("all");
              }}
              sx={{ textTransform: "none" }}
            >
              Clear filters
            </Button>
          )}
        </Box>

        {/* Songs Grid */}
        {filteredSongs.length > 0 ? (
          <Grid
            container
            spacing={2.5}
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {filteredSongs.map((song, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={song.id}>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.03, 0.4) }}
                >
                  <Card
                    component={Link}
                    to={`/Song?id=${song.id}`}
                    sx={{
                      textDecoration: "none",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2.5,
                      border: "1px solid #e2e8f0",
                      background: theme.palette.background.paper,
                      ":hover": {
                        boxShadow: "0 8px 24px rgba(37, 99, 235, 0.12)",
                        transform: "translateY(-3px)",
                        borderColor: "primary.light",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <CardHeader
                      title={song.title}
                      subheader={
                        <Typography
                          color="primary.main"
                          variant="body2"
                          fontWeight={500}
                        >
                          {song.artist}
                        </Typography>
                      }
                      titleTypographyProps={{
                        fontWeight: "bold",
                        variant: "h6",
                        color: "text.primary",
                      }}
                    />
                    <CardContent sx={{ mt: "auto", pt: 0 }}>
                      <Box mb={1} display="flex" gap={1} flexWrap="wrap">
                        {song.difficulty && (
                          <Chip
                            label={song.difficulty}
                            color={getDifficultyColor(song.difficulty)}
                            size="small"
                          />
                        )}
                        {song.key && (
                          <Chip
                            label={`Key: ${song.key}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {song.capo && (
                          <Chip
                            label={`Capo: ${song.capo}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                        {song.url && (
                          <Chip
                            color="info"
                            size="small"
                            label={getOriginalWebsite(song.url)}
                          />
                        )}
                      </Box>
                      {song.tags && song.tags.length > 0 && (
                        <Box mb={1} display="flex" gap={0.5} flexWrap="wrap">
                          {song.tags.slice(0, 3).map((t) => (
                            <Chip
                              key={t}
                              label={t}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                          {song.tags.length > 3 && (
                            <Chip
                              label={`+${song.tags.length - 3}`}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Box>
                      )}
                      {song.created_at && (
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          color="text.secondary"
                          fontSize="0.75rem"
                          mt={1}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {new Date(song.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Card
            variant="outlined"
            sx={{ textAlign: "center", p: 6, borderRadius: 2 }}
          >
            <MusicIcon color="disabled" sx={{ fontSize: 56, mb: 1.5 }} />
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              No songs matched your filters
            </Typography>
            <Typography color="text.secondary" mb={2}>
              Try adjusting your search query or difficulty filters
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchTerm("");
                setSelectedDifficulty("all");
                setSelectedTag("all");
              }}
              sx={{ textTransform: "none" }}
            >
              Reset Filters
            </Button>
          </Card>
        )}
      </Container>

      {/* Scrape Modal */}
      <ScrapeModal
        open={isScrapeOpen}
        onClose={() => setIsScrapeOpen(false)}
        onSuccess={() => loadArtistData()}
      />
    </Box>
  );
}

function SkeletonCard() {
  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <CardHeader
        title={
          <Box
            sx={{
              bgcolor: "grey.300",
              height: 24,
              width: "60%",
              borderRadius: 1,
            }}
          />
        }
        subheader={
          <Box
            sx={{
              bgcolor: "grey.200",
              height: 16,
              width: "40%",
              mt: 1,
              borderRadius: 1,
            }}
          />
        }
      />
      <CardContent>
        <Box sx={{ bgcolor: "grey.200", height: 16, mb: 1, borderRadius: 1 }} />
        <Box display="flex" gap={1}>
          <Box
            sx={{
              bgcolor: "grey.300",
              height: 24,
              width: 64,
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              bgcolor: "grey.300",
              height: 24,
              width: 80,
              borderRadius: 1,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
